/**
 * Apparel / Retail POS Screen  (Screen ID: 6.16.11)
 * ─────────────────────────────────────────────────────────────────────────
 * Design: pixel-perfect match to pos-foodcourt-workspaces.html reference.
 * Same CSS variables, same layout (330px | 1fr | 150px), same font (Manrope).
 * Industry: Apparel, Retail, Supermarket (configurable via `industry` prop).
 * Data: Mock — real API wiring pending user's .NET server endpoints.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ApparelPOS.css';

// ─── Mock Catalogue ────────────────────────────────────────────────────────
const CATALOGUE = {
  Shirts:      [
    { code:'SH001', nm:'Men Formal Shirt',    mrp:1299, rate:1099, hsn:'6205', gst:5,  sizes:['S','M','L','XL','XXL'], colors:['White','Blue','Black'],  icon:'👔' },
    { code:'SH002', nm:'Cotton Linen Shirt',  mrp:999,  rate:849,  hsn:'6205', gst:5,  sizes:['S','M','L','XL'],       colors:['White','Beige'],         icon:'👕' },
    { code:'SH003', nm:'Casual Check Shirt',  mrp:799,  rate:649,  hsn:'6205', gst:5,  sizes:['S','M','L','XL','XXL'], colors:['Red','Blue','Green'],    icon:'🧥' },
  ],
  'T-Shirts':  [
    { code:'TT001', nm:'Round Neck T-Shirt',  mrp:499,  rate:399,  hsn:'6109', gst:5,  sizes:['S','M','L','XL','XXL'], colors:['Black','Navy','Grey','Red'], icon:'👕' },
    { code:'TT002', nm:'Polo T-Shirt',        mrp:699,  rate:549,  hsn:'6109', gst:5,  sizes:['S','M','L','XL'],       colors:['White','Blue','Green'],  icon:'🎽' },
    { code:'TT003', nm:'Graphic T-Shirt',     mrp:549,  rate:449,  hsn:'6109', gst:5,  sizes:['S','M','L','XL'],       colors:['Black','White'],         icon:'👕' },
  ],
  Trousers:    [
    { code:'TR001', nm:'Formal Trouser',      mrp:1499, rate:1199, hsn:'6203', gst:12, sizes:['28','30','32','34','36'], colors:['Black','Grey','Navy'],  icon:'👖' },
    { code:'TR002', nm:'Chino Trouser',       mrp:1299, rate:999,  hsn:'6203', gst:12, sizes:['28','30','32','34','36'], colors:['Beige','Olive','Blue'], icon:'👖' },
  ],
  Jeans:       [
    { code:'JN001', nm:'Slim Fit Jeans',      mrp:1799, rate:1399, hsn:'6203', gst:12, sizes:['28','30','32','34','36'], colors:['Blue','Black','Grey'],  icon:'👖' },
    { code:'JN002', nm:'Straight Jeans',      mrp:1599, rate:1249, hsn:'6203', gst:12, sizes:['28','30','32','34'],      colors:['Blue','Black'],         icon:'🩳' },
  ],
  Kurtis:      [
    { code:'KU001', nm:'Cotton Kurti',        mrp:799,  rate:649,  hsn:'6211', gst:5,  sizes:['S','M','L','XL','XXL'], colors:['Pink','Yellow','Green'], icon:'👗' },
    { code:'KU002', nm:'Designer Kurti',      mrp:1299, rate:999,  hsn:'6211', gst:5,  sizes:['S','M','L','XL'],       colors:['Red','Blue','Purple'],   icon:'👘' },
  ],
  Sarees:      [
    { code:'SA001', nm:'Cotton Saree',        mrp:1999, rate:1649, hsn:'5208', gst:5,  sizes:['Free'],                  colors:['Blue','Green','Pink'],  icon:'🥻' },
    { code:'SA002', nm:'Silk Saree',          mrp:4999, rate:3999, hsn:'5007', gst:12, sizes:['Free'],                  colors:['Red','Yellow','Maroon'], icon:'🧣' },
  ],
  Kids:        [
    { code:'KD001', nm:'Kids T-Shirt',        mrp:299,  rate:249,  hsn:'6109', gst:5,  sizes:['2Y','4Y','6Y','8Y'],    colors:['Red','Blue','Yellow'],   icon:'👶' },
    { code:'KD002', nm:'Kids Jeans',          mrp:699,  rate:549,  hsn:'6203', gst:12, sizes:['2Y','4Y','6Y','8Y'],    colors:['Blue','Black'],          icon:'🧒' },
    { code:'KD003', nm:'Kids Frock',          mrp:499,  rate:399,  hsn:'6211', gst:5,  sizes:['2Y','4Y','6Y','8Y'],    colors:['Pink','Purple'],         icon:'👧' },
  ],
  Accessories: [
    { code:'AC001', nm:'Leather Belt',        mrp:499,  rate:399,  hsn:'4205', gst:18, sizes:['Free'],                  colors:['Black','Brown'],        icon:'👜' },
    { code:'AC002', nm:'Formal Socks',        mrp:199,  rate:149,  hsn:'6115', gst:12, sizes:['Free'],                  colors:['Black','White','Grey'], icon:'🧦' },
    { code:'AC003', nm:'Cap / Hat',           mrp:349,  rate:279,  hsn:'6505', gst:12, sizes:['Free'],                  colors:['Black','Navy'],         icon:'🧢' },
  ],
};
const ALL_ITEMS   = Object.values(CATALOGUE).flat();
const CATEGORIES  = ['All', ...Object.keys(CATALOGUE)];

const PAYMENT_MODES = [
  { id:'cash',   lb:'Cash',    ic:'💵' },
  { id:'card',   lb:'Card',    ic:'💳' },
  { id:'upi',    lb:'UPI',     ic:'📱' },
  { id:'wallet', lb:'Wallet',  ic:'👛' },
  { id:'credit', lb:'Credit',  ic:'📋' },
  { id:'split',  lb:'Split',   ic:'✂' },
];

const MOCK_CUSTOMERS = [
  { id:'C001', name:'Rajesh Kumar',  mobile:'98450 11223', tier:'Gold',     points:1250 },
  { id:'C002', name:'Priya Sharma',  mobile:'90080 33445', tier:'Platinum', points:4500 },
  { id:'C003', name:'Anita Singh',   mobile:'93960 78901', tier:'Silver',   points:320  },
  { id:'C004', name:'Vikram Nair',   mobile:'77801 22334', tier:'Gold',     points:1890 },
  { id:'C005', name:'Meena Pillai',  mobile:'94456 77889', tier:'Regular',  points:50   },
];

const PROMOS = [
  { code:'WELCOME10',  desc:'10% off on total bill for new customers',  pct:10,  flat:0   },
  { code:'FLAT200',    desc:'Flat ₹200 off on bills above ₹2000',        pct:0,   flat:200 },
  { code:'SUMMER15',   desc:'15% off on Shirts & T-Shirts',              pct:15,  flat:0   },
  { code:'BOGO50',     desc:'Buy 2 get 50% off on 3rd item',             pct:50,  flat:0   },
];

function fmt(n) {
  return (isNaN(n) ? 0 : n).toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 });
}
function nowStr() { return new Date().toLocaleTimeString('en-IN', { hour12:false }); }
function todayStr() { return new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); }

// ─── Variant Picker Modal ─────────────────────────────────────────────────
function VariantModal({ item, onAdd, onClose }) {
  const [size,  setSize]  = useState(item.sizes[0]);
  const [color, setColor] = useState(item.colors[0]);
  const [qty,   setQty]   = useState(1);
  const [disc,  setDisc]  = useState(0);

  const discAmt = (item.rate * qty * disc) / 100;
  const taxAmt  = ((item.rate * qty - discAmt) * item.gst) / 100;
  const net     = item.rate * qty - discAmt + taxAmt;

  return (
    <div className="apos-modal-overlay" onClick={onClose}>
      <div className="apos-modal-box" onClick={e => e.stopPropagation()}>
        <div className="apos-modal-head">
          <div>
            <div className="apos-modal-title">{item.nm}</div>
            <div className="apos-modal-subtitle">{item.code} · HSN {item.hsn} · GST {item.gst}%</div>
          </div>
          <button className="apos-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="apos-modal-body">
          <div className="apos-modal-group">
            <label>Size</label>
            <div className="apos-chips">
              {item.sizes.map(s => (
                <button key={s} className={`apos-chip${size===s?' sel':''}`} onClick={() => setSize(s)}>{s}</button>
              ))}
            </div>
          </div>
          <div className="apos-modal-group">
            <label>Colour</label>
            <div className="apos-chips">
              {item.colors.map(c => (
                <button key={c} className={`apos-chip${color===c?' sel':''}`} onClick={() => setColor(c)}>{c}</button>
              ))}
            </div>
          </div>
          <div className="apos-modal-grid">
            <div className="apos-modal-field">
              <label>MRP</label>
              <input value={`₹${item.mrp}`} readOnly />
            </div>
            <div className="apos-modal-field">
              <label>Rate</label>
              <input value={item.rate} readOnly />
            </div>
            <div className="apos-modal-field">
              <label>Qty</label>
              <input type="number" min="1" value={qty}
                onChange={e => setQty(Math.max(1, +e.target.value))} autoFocus />
            </div>
            <div className="apos-modal-field">
              <label>Disc %</label>
              <input type="number" min="0" max="100" value={disc}
                onChange={e => setDisc(Math.max(0, Math.min(100, +e.target.value)))} />
            </div>
          </div>
          <div className="apos-modal-summary">
            <span>Gross: <b>₹{fmt(item.rate * qty)}</b></span>
            <span>Disc: <b>₹{fmt(discAmt)}</b></span>
            <span>GST ({item.gst}%): <b>₹{fmt(taxAmt)}</b></span>
            <span>Net: <b>₹{fmt(net)}</b></span>
          </div>
        </div>

        <div className="apos-modal-foot">
          <button className="apos-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="apos-modal-add"
            onClick={() => onAdd({ ...item, size, color, qty, discAmt, taxAmt, lineAmt: net })}>
            + Add to Bill
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────
export default function ApparelPOSScreen({ theme, navigateTo }) {
  const [activeWs,    setActiveWs]    = useState('products');
  const [activeCat,   setActiveCat]   = useState('All');
  const [searchQ,     setSearchQ]     = useState('');
  const [order,       setOrder]       = useState([]);
  const [customer,    setCustomer]    = useState(null);
  const [custSearch,  setCustSearch]  = useState('');
  const [payMethod,   setPayMethod]   = useState(null);
  const [tendered,    setTendered]    = useState('');
  const [promo,       setPromo]       = useState(null);
  const [modalItem,   setModalItem]   = useState(null);
  const [clock,       setClock]       = useState(nowStr());
  const [toastMsg,    setToastMsg]    = useState('');
  const [toastOn,     setToastOn]     = useState(false);
  const toastTimer = useRef(null);

  // Clock tick
  useEffect(() => {
    const t = setInterval(() => setClock(nowStr()), 1000);
    return () => clearInterval(t);
  }, []);

  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setToastOn(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastOn(false), 1800);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'F10') { e.preventDefault(); handleSave(); }
      if (e.key === 'F2')  { e.preventDefault(); voidLastRow(); }
      if (e.key === 'F5')  { e.preventDefault(); showToast('Bill held'); }
      if (e.key === 'F1')  { e.preventDefault(); showToast('Retrieving held bill…'); }
      if (e.key === 'F4')  { e.preventDefault(); setActiveWs('payment'); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  // Filtered items
  const tiles = (() => {
    const pool = activeCat === 'All' ? ALL_ITEMS : (CATALOGUE[activeCat] || []);
    if (!searchQ.trim()) return pool;
    const q = searchQ.toLowerCase();
    return pool.filter(it => it.nm.toLowerCase().includes(q) || it.code.toLowerCase().includes(q));
  })();

  // Totals
  const totQty   = order.reduce((s, r) => s + r.qty, 0);
  const gross    = order.reduce((s, r) => s + r.rate * r.qty, 0);
  const totDisc  = order.reduce((s, r) => s + (r.discAmt || 0), 0);
  const promoAmt = promo ? (promo.flat > 0 ? (gross >= 2000 ? promo.flat : 0) : (gross - totDisc) * promo.pct / 100) : 0;
  const totTax   = order.reduce((s, r) => s + (r.taxAmt || 0), 0);
  const afterAll = gross - totDisc - promoAmt + totTax;
  const roundOff = Math.round(afterAll) - afterAll;
  const grandTotal = Math.round(afterAll);
  const change   = Math.max(0, (+tendered || 0) - grandTotal);

  function addToOrder(variant) {
    setOrder(prev => {
      const idx = prev.findIndex(r => r.code === variant.code && r.size === variant.size && r.color === variant.color);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          qty:     updated[idx].qty + variant.qty,
          discAmt: (updated[idx].discAmt || 0) + variant.discAmt,
          taxAmt:  (updated[idx].taxAmt  || 0) + variant.taxAmt,
          lineAmt: (updated[idx].lineAmt || 0) + variant.lineAmt,
        };
        return updated;
      }
      return [...prev, variant];
    });
    setModalItem(null);
    showToast(`${variant.nm} (${variant.size}/${variant.color}) added`);
  }

  function voidLastRow() {
    if (!order.length) { showToast('No items to void'); return; }
    setOrder(prev => prev.slice(0, -1));
    showToast('Last row voided (F2)');
  }

  function handleSave() {
    if (!order.length) { showToast('Add items before saving'); return; }
    showToast('Bill saved — proceeding to payment');
    setActiveWs('payment');
  }

  function handleClear() {
    setOrder([]); setCustomer(null); setPromo(null);
    setTendered(''); setPayMethod(null);
    showToast('Bill cleared');
  }

  function confirmPayment() {
    if (!tendered || +tendered < grandTotal) { showToast('Enter tendered amount ≥ Grand Total'); return; }
    showToast(`Payment confirmed! Change ₹${fmt(change)}`);
    setTimeout(handleClear, 1600);
  }

  const filtCustomers = custSearch.trim()
    ? MOCK_CUSTOMERS.filter(c =>
        c.name.toLowerCase().includes(custSearch.toLowerCase()) || c.mobile.includes(custSearch))
    : MOCK_CUSTOMERS;

  return (
    <div className="apos-root" data-testid="apparel-pos-screen">
      {/* ── MAIN ROW ─────────────────────────────────────────────────── */}
      <div className="apos-main">

        {/* ═══════ LEFT: ORDER PANEL ════════════════════════════════════ */}
        <div className="apos-order">
          <div className="apos-order-head">
            <span>Item</span>
            <span style={{textAlign:'center'}}>Qty</span>
            <span style={{textAlign:'right'}}>Value</span>
          </div>

          <div className="apos-order-list">
            {order.length === 0
              ? <div className="apos-order-empty">No items yet — select from Product Selection</div>
              : order.map((r, i) => (
                <div className="apos-order-row" key={i} data-testid={`order-row-${i}`}>
                  <div>
                    <span className="apos-row-nm">{r.nm}</span>
                    <span className="apos-row-meta">{r.size} · {r.color}</span>
                  </div>
                  <span className="apos-row-qty">{r.qty}</span>
                  <span className="apos-row-val">
                    {fmt(r.lineAmt)}
                    <button className="apos-row-del" onClick={() => setOrder(prev => prev.filter((_, j) => j !== i))}>✕</button>
                  </span>
                </div>
              ))
            }
          </div>

          <div className="apos-order-total-line">
            <span className="apos-mono">{totQty}</span>
            <span className="apos-mono">{fmt(gross)}</span>
          </div>

          <div className="apos-order-actions">
            <div className="apos-oa-btn" onClick={() => showToast('Loading old bills…')} data-testid="old-bill-btn">
              <div className="apos-oa-ic">📌</div><div className="apos-oa-lb">Old Bill</div>
            </div>
            <div className="apos-oa-btn" onClick={voidLastRow} data-testid="row-void-btn">
              <div className="apos-oa-ic">🗑</div><div className="apos-oa-lb">Row Void (F2)</div>
            </div>
            <div className="apos-oa-btn" onClick={() => showToast('Bill held (F5)')} data-testid="hold-btn">
              <div className="apos-oa-ic">📁</div><div className="apos-oa-lb">Hold (F5)</div>
            </div>
            <div className="apos-oa-btn" onClick={() => showToast('Retrieving hold…')} data-testid="get-hold-btn">
              <div className="apos-oa-ic">🗂</div><div className="apos-oa-lb">Get Hold (F1)</div>
            </div>
          </div>

          <div className="apos-info-grid">
            <div className="apos-info-item editable">
              <label>Name</label>
              <input defaultValue={customer ? customer.name : 'Walk-in'} key={customer?.id} />
            </div>
            <div className="apos-info-item">
              <label>Total Qty</label>
              <span className="apos-mono" id="infoTotalQty">{fmt(totQty)}</span>
            </div>
            <div className="apos-info-item editable">
              <label>Mobile No</label>
              <input className="apos-mono" defaultValue={customer ? customer.mobile : ''} key={`mob-${customer?.id}`} />
            </div>
            <div className="apos-info-item">
              <label>Excl VAT</label>
              <span className="apos-mono">{fmt(gross - totDisc - promoAmt)}</span>
            </div>
            <div className="apos-info-item">
              <label>Salesman</label>
              <span>Counter</span>
            </div>
            <div className="apos-info-item">
              <label>Incl VAT</label>
              <span className="apos-mono">{fmt(afterAll)}</span>
            </div>
            <div className="apos-info-item">
              <label>Gross Amt</label>
              <span className="apos-mono">{fmt(gross)}</span>
            </div>
            <div className="apos-info-item">
              <label>Round Off</label>
              <span className="apos-mono">{roundOff.toFixed(2)}</span>
            </div>
          </div>

          <div className="apos-total-bar">
            <span>Total Amount</span>
            <span>
              <span className="apos-cur">INR</span>
              <span className="apos-mono" data-testid="grand-total">₹{fmt(grandTotal)}</span>
            </span>
          </div>
        </div>

        {/* ═══════ CENTER: WORKSPACE ════════════════════════════════════ */}
        <div className="apos-workspace">
          <div className="apos-ws-switch">
            {[
              { id:'products', ic:'🏷', lb:'Product Selection'  },
              { id:'payment',  ic:'💳', lb:'Payment Options'    },
              { id:'customer', ic:'👤', lb:'Customer Profile'   },
              { id:'promo',    ic:'🎁', lb:'Schemes & Offers'   },
            ].map(t => (
              <button key={t.id}
                className={activeWs === t.id ? 'active' : ''}
                onClick={() => setActiveWs(t.id)}
                data-testid={`tab-${t.id}`}
              >
                {t.ic} <span className="lbl-txt">{t.lb}</span>
              </button>
            ))}
          </div>

          <div className="apos-ws-panel">

            {/* ── PRODUCTS ── */}
            {activeWs === 'products' && (
              <div style={{display:'flex',flexDirection:'column',flex:1,minHeight:0,overflow:'hidden'}}>
                <div className="apos-prod-head">
                  <input
                    placeholder="🔍 Scan barcode or search item / code…"
                    value={searchQ}
                    onChange={e => setSearchQ(e.target.value)}
                    data-testid="item-search"
                  />
                </div>

                <div className="apos-cat-tabs">
                  <button className="apos-cat-arrow on">‹</button>
                  <div className="apos-cat-strip">
                    {CATEGORIES.map(c => (
                      <button key={c}
                        className={activeCat === c ? 'active' : ''}
                        onClick={() => setActiveCat(c)}
                        data-testid={`cat-${c}`}
                      >{c}</button>
                    ))}
                  </div>
                  <button className="apos-cat-arrow">›</button>
                </div>

                <div className="apos-tile-grid" data-testid="tile-grid">
                  {tiles.map(it => (
                    <div key={it.code} className="apos-tile"
                      onClick={() => setModalItem(it)}
                      data-testid={`tile-${it.code}`}>
                      <div className="apos-tile-sw">{it.icon}</div>
                      <div className="apos-tile-code">{it.code}</div>
                      <div className="apos-tile-nm">{it.nm}</div>
                      <div className="apos-tile-sizes">{it.sizes.slice(0,4).join(' · ')}{it.sizes.length > 4 ? '…' : ''}</div>
                      <div className="apos-tile-pr">
                        <s>₹{it.mrp}</s>{' '}
                        <strong>₹{it.rate}</strong>
                      </div>
                    </div>
                  ))}
                  {tiles.length === 0 && (
                    <div className="apos-tile-empty">No items found</div>
                  )}
                </div>
              </div>
            )}

            {/* ── PAYMENT ── */}
            {activeWs === 'payment' && (
              <div className="apos-pay-body">
                <div className="apos-pay-due">
                  <span className="apos-lbl">Amount Due</span>
                  <span className="apos-amt apos-mono" data-testid="pay-due">₹{fmt(grandTotal)}</span>
                </div>

                <div className="apos-pay-methods">
                  {PAYMENT_MODES.map(m => (
                    <div key={m.id}
                      className={`apos-pm-card${payMethod === m.id ? ' active' : ''}`}
                      onClick={() => setPayMethod(m.id)}
                      data-testid={`pay-${m.id}`}>
                      <div className="apos-pm-ic">{m.ic}</div>
                      <div className="apos-pm-lb">{m.lb}</div>
                    </div>
                  ))}
                </div>

                <div className="apos-tender-row">
                  <div className="apos-tender-box">
                    <label>Tendered Amount</label>
                    <input className="apos-mono" type="number" placeholder="0.00"
                      value={tendered} onChange={e => setTendered(e.target.value)}
                      data-testid="tendered-input" />
                  </div>
                  <div className="apos-tender-box">
                    <label>Change Due</label>
                    <input className="apos-mono" value={fmt(change)} readOnly data-testid="change-due" />
                  </div>
                </div>

                <div className="apos-quick-amt">
                  {[500,1000,2000,5000].map(a => (
                    <button key={a} onClick={() => setTendered(String(a))}>₹{a}</button>
                  ))}
                  <button className="exact-btn" onClick={() => setTendered(String(grandTotal))}>Exact</button>
                </div>

                <button className="apos-confirm-pay" onClick={confirmPayment} data-testid="confirm-pay-btn">
                  ✓ Confirm Payment — ₹{fmt(grandTotal)}
                </button>

                <div className="apos-pay-summary">
                  {[
                    ['Subtotal',  fmt(gross)],
                    ['Discount',  '-₹'+fmt(totDisc+promoAmt)],
                    ['GST',       '₹'+fmt(totTax)],
                    ['Round Off', roundOff.toFixed(2)],
                    ['Grand Total','₹'+fmt(grandTotal)],
                  ].map(([k,v]) => (
                    <div key={k} className="apos-ps-row">
                      <span>{k}</span><span className="apos-ps-val">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── CUSTOMER ── */}
            {activeWs === 'customer' && (
              <div className="apos-cust-body">
                <div className="apos-cust-search">
                  <input placeholder="Search by name or mobile…"
                    value={custSearch} onChange={e => setCustSearch(e.target.value)}
                    data-testid="cust-search" />
                </div>
                <div className="apos-cust-list">
                  {filtCustomers.map(c => (
                    <div key={c.id} className={`apos-cust-card${customer?.id===c.id?' selected':''}`}>
                      <div>
                        <span className="apos-cust-nm">{c.name}</span>
                        <span className={`apos-cust-tier ${c.tier.toLowerCase()}`}>{c.tier}</span>
                        <div className="apos-cust-ph apos-mono">{c.mobile}</div>
                        <div className="apos-cust-pts">{c.points} loyalty pts</div>
                      </div>
                      <button onClick={() => { setCustomer(c); setActiveWs('products'); showToast(`Customer: ${c.name}`); }}
                        data-testid={`sel-cust-${c.id}`}>Select</button>
                    </div>
                  ))}
                </div>
                <div className="apos-new-cust">
                  <h3>+ New Customer</h3>
                  <div className="apos-new-grid">
                    <input placeholder="Full name" data-testid="new-cust-name" />
                    <input placeholder="Mobile number" data-testid="new-cust-mobile" />
                    <input placeholder="Email (optional)" />
                    <input placeholder="GSTIN (optional)" />
                  </div>
                  <button onClick={() => showToast('Customer saved')} data-testid="save-cust-btn">Save Customer</button>
                </div>
              </div>
            )}

            {/* ── PROMO ── */}
            {activeWs === 'promo' && (
              <div className="apos-promo-body">
                <div className={`apos-promo-active${promo ? ' show' : ''}`}>
                  <span className="apos-pa-l">✓ <b>{promo?.code}</b> applied</span>
                  <button onClick={() => { setPromo(null); showToast('Promo removed'); }}>Remove</button>
                </div>
                <div className="apos-promo-list">
                  {PROMOS.map(p => (
                    <div key={p.code} className={`apos-promo-card${promo?.code===p.code?' applied':''}`}>
                      <div>
                        <span className="apos-promo-code">{p.code}</span>
                        <div className="apos-promo-desc">{p.desc}</div>
                      </div>
                      <button
                        onClick={() => {
                          if (promo?.code === p.code) { setPromo(null); showToast('Promo removed'); }
                          else { setPromo(p); showToast(`${p.code} applied`); }
                        }}
                        data-testid={`promo-${p.code}`}>
                        {promo?.code === p.code ? 'Remove' : 'Apply'}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="apos-manual-promo">
                  <input placeholder="Enter promo code manually…" data-testid="manual-promo" />
                  <button onClick={() => showToast('Promo applied')}>Apply</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════ RIGHT: FUNCTION RAIL ══════════════════════════════════ */}
        <div className="apos-func-rail">
          <div className="apos-fr-btn" onClick={() => showToast('More options…')} data-testid="rail-more">
            <div className="apos-fr-ic">⋮</div><div className="apos-fr-lb">More</div>
          </div>
          <div className="apos-fr-btn" onClick={() => showToast('Exchange bill…')} data-testid="rail-exchange">
            <div className="apos-fr-ic">🔄</div><div className="apos-fr-lb">Exchange</div>
          </div>
          <div className="apos-fr-btn" onClick={() => showToast('Return bill…')} data-testid="rail-return">
            <div className="apos-fr-ic">↩</div><div className="apos-fr-lb">Return</div>
          </div>
          <div className="apos-fr-btn" onClick={() => showToast('Printing bill…')} data-testid="rail-print">
            <div className="apos-fr-ic">🖨</div><div className="apos-fr-lb">Print Bill</div>
          </div>
          <div className="apos-fr-btn" onClick={() => showToast('Lay-By…')} data-testid="rail-layby">
            <div className="apos-fr-ic">📦</div><div className="apos-fr-lb">Lay-By</div>
          </div>
          <div className="apos-fr-btn" onClick={() => showToast('Bill discount…')} data-testid="rail-disc">
            <div className="apos-fr-ic">🏷</div><div className="apos-fr-lb">Bill Disc.</div>
          </div>
          <div className="apos-fr-btn save" onClick={handleSave} data-testid="rail-save">
            <div className="apos-fr-ic">💾</div><div className="apos-fr-lb">Save (F10)</div>
          </div>
          <div className="apos-fr-btn" onClick={handleClear} data-testid="rail-clear">
            <div className="apos-fr-ic">🔄</div><div className="apos-fr-lb">Clear</div>
          </div>
          <div className="apos-fr-btn exit" onClick={() => navigateTo && navigateTo('HUB')} data-testid="rail-exit">
            <div className="apos-fr-ic">⏻</div><div className="apos-fr-lb">Exit</div>
          </div>
        </div>
      </div>

      {/* ── STATUS BAR ──────────────────────────────────────────────────── */}
      <div className="apos-statusbar">
        <span>Organisation: <b>RetailCLIQ ERP</b></span>
        <span>ScreenId <b>6.16.11</b></span>
        <span>Caption: <b>Apparel POS Billing</b></span>
        <span>User: <b>SSADMIN</b> <span className="apos-mono">{clock}</span></span>
        <span>Business Day: <b className="apos-mono">{todayStr()}</b></span>
        <span>Fin. Year: <b>2025–2026</b></span>
        <div className="apos-sb-spacer">
          <div className="apos-sb-icon">?</div>
          <div className="apos-sb-icon">💬</div>
          <div className="apos-sb-icon">📅</div>
        </div>
      </div>

      {/* ── VARIANT MODAL ───────────────────────────────────────────────── */}
      {modalItem && <VariantModal item={modalItem} onAdd={addToOrder} onClose={() => setModalItem(null)} />}

      {/* ── TOAST ───────────────────────────────────────────────────────── */}
      <div className={`apos-toast${toastOn ? ' show' : ''}`} data-testid="toast">{toastMsg}</div>
    </div>
  );
}
